import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertCircle, CheckCircle2, Globe, Loader2, Phone, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '@/hooks/useTenant';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useTrackingNumbers, type TrackingNumber } from '@/hooks/useTrackingNumbers';

const PURPOSE_BADGE: Record<string, { label: string; className: string }> = {
  main: { label: 'Main line', className: 'bg-primary/10 text-primary border-primary/20' },
  google_ads_pool: { label: 'Google Ads', className: 'bg-blue-500/10 text-blue-600 border-blue-200' },
  organic: { label: 'Organic', className: 'bg-green-500/10 text-green-600 border-green-200' },
  direct: { label: 'Direct', className: 'bg-secondary text-muted-foreground' },
  offline: { label: 'Offline', className: 'bg-amber-500/10 text-amber-600 border-amber-200' },
  custom: { label: 'Other', className: 'bg-secondary text-muted-foreground' },
};

/** Settings → Phone & tracking: the numbers Steddy holds for this workspace. */
export function PhoneTracking() {
  const { company } = useTenant();
  const { formatShortDate } = useCompanyLocale();
  const {
    numbers, isLoading, provisioning, updateRouting, isSavingRouting,
    checkSite, isCheckingSite, siteCheck, refetch,
  } = useTrackingNumbers();
  const [editing, setEditing] = useState<TrackingNumber | null>(null);

  const managed = (company as { ctm_mode?: string } | undefined)?.ctm_mode === 'agency';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Phone &amp; tracking</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The numbers on your ads, your website and your vans, and where each one rings.
        </p>
      </div>

      {provisioning?.status === 'partial' && (
        <div className="flex gap-2 rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm">
          <AlertCircle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Phone setup did not finish</div>
            <div className="text-muted-foreground">
              {provisioning.error || 'Some steps did not complete.'} Nothing was lost; support can pick up where it stopped.
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Your numbers
          </CardTitle>
          <CardDescription>
            {managed
              ? 'Steddy holds these for you. Calls and texts are billed by use on your monthly invoice.'
              : 'Your numbers live in your own CallTrackingMetrics account.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : numbers.length === 0 ? (
            <div className="text-center py-10">
              <Phone className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-base font-semibold mb-1">No Steddy-managed numbers yet</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {managed
                  ? 'Your phone setup is being prepared.'
                  : 'This workspace uses its own CallTrackingMetrics account. Numbers you buy there will not appear here.'}
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Number</TableHead>
                    <TableHead>What it is</TableHead>
                    <TableHead>Rings</TableHead>
                    <TableHead className="text-right">Calls</TableHead>
                    <TableHead className="text-right">Texts</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {numbers.map((n) => {
                    const badge = PURPOSE_BADGE[n.purpose] ?? PURPOSE_BADGE.custom;
                    return (
                      <TableRow key={n.id}>
                        <TableCell className="font-medium">
                          {n.phone_e164}
                          {n.is_test && <Badge variant="secondary" className="ml-2">Test</Badge>}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={badge.className}>{badge.label}</Badge>
                            <span className="text-xs text-muted-foreground">{n.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {n.answer_mode === 'softphone' ? (
                            <span className="text-sm">In the Steddy softphone</span>
                          ) : (
                            <span className="text-sm">{n.forward_to_e164 || '—'}</span>
                          )}
                          {n.routing_pending && (
                            <Badge variant="outline" className="ml-2 bg-amber-500/10 text-amber-600 border-amber-200">
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{n.calls_this_month}</TableCell>
                        <TableCell className="text-right">
                          {n.sms_enabled ? (
                            n.texts_this_month
                          ) : (
                            <span className="text-muted-foreground" title="This number can take calls but not texts">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => setEditing(n)} aria-label="Change where this rings">
                            <Settings2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {numbers.length > 0 && numbers.every((n) => !n.sms_enabled) && (
            <p className="mt-3 text-xs text-muted-foreground">
              These numbers take calls but not texts. Australian tracking numbers are voice-only, so you are
              never charged for texting on them.
            </p>
          )}

          {numbers.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              Calls counted this month. Releasing a number is done by support and stops its billing that day.
              {numbers.some((n) => n.next_billing_date) && (
                <> Next renewal {formatShortDate(numbers.find((n) => n.next_billing_date)!.next_billing_date!)}.</>
              )}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Tracking on your website
          </CardTitle>
          <CardDescription>
            The tracking script swaps the number on your site to the right tracking number, which is how a
            phone call gets matched back to the ad that earned it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            It needs to be on every page, just before the closing head tag, or added through Google Tag Manager.
            The number also has to be real text on the page. If it is part of an image, there is nothing to swap.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => checkSite(undefined).catch((e: Error) => toast.error(e.message))} disabled={isCheckingSite}>
              {isCheckingSite ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Checking</>
              ) : 'Check my site'}
            </Button>
            <span className="text-xs text-muted-foreground">
              {company?.website ? `We will load ${company.website}` : 'Add your website to the company profile first'}
            </span>
          </div>

          {siteCheck && (
            <div
              className={`flex gap-2 rounded-md border p-3 text-sm ${
                siteCheck.healthy
                  ? 'border-green-200 bg-green-500/5'
                  : 'border-amber-200 bg-amber-500/5'
              }`}
            >
              {siteCheck.healthy
                ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                : <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />}
              <div className="space-y-1">
                <div>{siteCheck.message}</div>
                {siteCheck.numbers_found && siteCheck.numbers_found.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    Showing on the page: {siteCheck.numbers_found.join(', ')}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <RoutingDialog
        number={editing}
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
        saving={isSavingRouting}
        onSave={async (answerMode, forwardTo) => {
          if (!editing) return;
          try {
            await updateRouting({ id: editing.id, answerMode, forwardTo });
            toast.success(
              answerMode === 'softphone'
                ? 'Saved. We will switch this number to the softphone shortly.'
                : 'Saved. We will point this number at the new destination shortly.',
            );
            setEditing(null);
            void refetch();
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not save');
          }
        }}
      />
    </div>
  );
}

function RoutingDialog({
  number, open, onOpenChange, saving, onSave,
}: {
  number: TrackingNumber | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  saving: boolean;
  onSave: (answerMode: 'forward' | 'softphone', forwardTo: string) => void | Promise<void>;
}) {
  const [answerMode, setAnswerMode] = useState<'forward' | 'softphone'>('forward');
  const [forwardTo, setForwardTo] = useState('');

  useEffect(() => {
    if (number) {
      setAnswerMode(number.answer_mode);
      setForwardTo(number.forward_to_e164 ?? '');
    }
  }, [number]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Where should {number?.phone_e164} ring?</DialogTitle>
          <DialogDescription>
            This changes the destination only. The number itself, and everything already tracked against it, stays the same.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="answer-mode">Answer on</Label>
            <Select value={answerMode} onValueChange={(v) => setAnswerMode(v as 'forward' | 'softphone')}>
              <SelectTrigger id="answer-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="forward">A phone or mobile</SelectItem>
                <SelectItem value="softphone">The Steddy softphone</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {answerMode === 'forward' ? (
            <div className="space-y-1">
              <Label htmlFor="forward-to">Ring this number</Label>
              <Input
                id="forward-to"
                value={forwardTo}
                onChange={(e) => setForwardTo(e.target.value)}
                placeholder="+61 2 1234 5678"
                inputMode="tel"
              />
              <p className="text-xs text-muted-foreground">Calls and texts cost the same wherever they ring.</p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Softphone answering rings whoever is signed in and on shift. It needs your team&apos;s
              CallTrackingMetrics agent setup, so this number shows as pending until that is in place.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(answerMode, forwardTo)} disabled={saving}>
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving</> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PhoneTracking;
